import React, { useMemo, useState } from "react"
import { toast } from "react-toastify"

import AccountInfo from "./components/AccountInfo"
import ConnectionList from "./components/ConnectionList"
import ConnectorStatus from "./components/ConnectorStatus"
import CustomConfigSection from "./components/CustomConfigSection"
import MetaCredentialSection from "./components/MetaCredentialSection"
import UploadedFilesSection from "./components/UploadedFilesSection"
import ConnectorDialog from "../ConnectorDialog"
import ResetSetupConfirmDialog from "../ResetSetupConfirmDialog"
import { CredentialGrantWarningChip } from "../CredentialGrantWarning"
import { Button } from "~/ui-rtk/components/ui/controls"
import { useConnect } from "./connect"
import { useConnect as useConnectorStatusConnect } from "./components/ConnectorStatus/connect"
import {
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
  useCompanyConnectorControllerGetAirbyteSetupErrorQuery,
  useCompanyConnectorControllerUpdateCompanyConnectorMutation,
} from "~/ui-rtk/api/companyConnectorApi"
import {
  mapConnectorStatusWithVersionCheck,
  isIngestionConnector,
  isMetaDataFallbackConnector,
  isMetaSystemUserConnector,
  RESETTABLE_STATUSES,
} from "~/ui-rtk/utils/connector-utils"
import { isConnectorVersionUpToDate } from "~/ui-rtk/utils/connector-version-utils"
import useConnectors from "~/ui-rtk/hooks/connectors"
import useAccess from "~/ui-rtk/hooks/access"
import { renderStatusIcon } from "./utils"
import {
  SetupErrorBanner,
  type AirbyteSetupErrorData,
  ERROR_ELIGIBLE_STATUSES,
} from "~/ui-rtk/components/features/connectors/SetupErrorBanner"

import type { TDialogProps } from "~/ui-rtk/components/ui/common/Dialog"
import type { ConnectorStatus as ConnectorStatusType } from "~/ui-rtk/shared/types/connectors"
import { useConnectorsTracking } from "~/ui-rtk/hooks/useConnectorsTracking"

type ConnectorStatusDialogProps = {
  imgSrc: string
  label: string
  confirmedAt?: string
  companyConnectorId: string
  allowMultipleConnections?: boolean
  onReconnect: (companyConnectorId?: string) => void
  onClose: () => void
  onAddConnection?: () => void
} & Pick<TDialogProps, "isOpen">

const ConnectionStatusDialog: React.FC<ConnectorStatusDialogProps> = ({
  isOpen,
  label,
  imgSrc,
  confirmedAt,
  companyConnectorId,
  allowMultipleConnections,
  onClose,
  onReconnect,
  onAddConnection,
}) => {
  const { isSuperAdmin } = useAccess()
  const { disconnect } = useConnect()
  const { connectors } = useConnectors()
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const {
    trackReconnectClicked,
    trackDisconnectClicked,
    trackRemoveMigrationRestrictions,
  } = useConnectorsTracking()
  const { data: connectedServices } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery()
  const [updateCompanyConnector, { isLoading: isUpdating }] =
    useCompanyConnectorControllerUpdateCompanyConnectorMutation()

  // Get connector health status
  const { connectorInfo, isLoading: isLoadingConnectorStatus } =
    useConnectorStatusConnect(companyConnectorId)

  // Find the current connector and determine its status
  const currentConnector = connectedServices?.find(
    service => service.id === companyConnectorId,
  )
  const connectorDefinition = connectors?.find(
    c => c.id === currentConnector?.serviceName,
  )

  const isVersionUpToDate =
    currentConnector && connectorDefinition
      ? isConnectorVersionUpToDate(connectorDefinition, currentConnector)
      : true

  // Determine unified connector status based on health and database status.
  // Live poller health takes precedence: an unhealthy or pending-setup connection
  // needs user action (reconnect / finish setup) regardless of scope version drift.
  // Healthy (or unknown) poller state delegates to the shared mapping, which only
  // bumps to UPGRADE when the DB status is CONNECTED and the version is behind.
  const getUnifiedConnectorStatus = () => {
    if (connectorInfo?.status === "unhealthy") {
      return "RECONNECT"
    }

    if (connectorInfo?.status === "pending_setup") {
      return "PENDING_SETUP"
    }

    return mapConnectorStatusWithVersionCheck(
      currentConnector?.status,
      isSuperAdmin,
      isVersionUpToDate,
    )
  }

  const connectorStatus = getUnifiedConnectorStatus()
  const isHealthy = connectorInfo?.status === "healthy"

  // Fetch error message if the computed status is one the backend persists
  // an error for (PENDING_SETUP / RECONNECT). Uses the same constant as
  // SourceItem so the card and dialog can't drift.
  const hasError = ERROR_ELIGIBLE_STATUSES.includes(
    connectorStatus as ConnectorStatusType,
  )
  const { data: errorDataRaw } =
    useCompanyConnectorControllerGetAirbyteSetupErrorQuery(
      { id: companyConnectorId },
      { skip: !hasError || !isOpen },
    )
  const errorData = errorDataRaw as AirbyteSetupErrorData | null | undefined

  // Check if there are multiple connectors for the same service
  const hasMultipleConnectors = useMemo(() => {
    if (!connectedServices || !currentConnector) return false
    return (
      connectedServices.filter(
        s => s.serviceName === currentConnector.serviceName,
      ).length > 1
    )
  }, [connectedServices, currentConnector])

  const isCreatedByMigration =
    currentConnector?.metadata?.createdByMigration === true

  // A Meta system user on the row: an APP-548 data fallback copy, or a legacy
  // pasted row until it is deleted. Super admins see which one in the Meta
  // credential section below.
  const isMetaSystemUser = isMetaSystemUserConnector(currentConnector?.metadata)
  // A healthy connector whose Meta grant no longer covers its account (see
  // CredentialGrantWarningChip), or whose data runs on the system-user
  // fallback, is only right again after a reconnect, so the healthy footer
  // offers one; unhealthy connectors already have it.
  // A system user has no per-person grant, so a warning left on its row is stale.
  const grantWarning = isMetaSystemUser
    ? null
    : (currentConnector?.metadata?.credentialGrantWarning ?? null)
  const grantNeedsReconnect =
    (!!grantWarning ||
      isMetaDataFallbackConnector(currentConnector?.metadata)) &&
    isHealthy

  // Check if this is an ingestion connector
  const isIngestion =
    connectorDefinition && isIngestionConnector(connectorDefinition)

  const handleReconnect = () => {
    if (isCreatedByMigration) {
      toast.error(
        "This connector was created by the system and cannot be re-authenticated. Please contact your administrator if you need to modify this connection.",
        {
          toastId: "migration-connector-reconnect-dialog-blocked",
        },
      )
      return
    }
    trackReconnectClicked({
      connector_name: label,
      company_connector_id: companyConnectorId,
      connector_status: connectorStatus,
      is_healthy: isHealthy,
      is_super_admin: isSuperAdmin,
      service_name: currentConnector?.serviceName,
    })
    void onReconnect(companyConnectorId)
    void onClose()
  }

  const handleDisconnect = async () => {
    trackDisconnectClicked({
      connector_name: label,
      company_connector_id: companyConnectorId,
      connector_status: connectorStatus,
      is_healthy: isHealthy,
      is_super_admin: isSuperAdmin,
      service_name: currentConnector?.serviceName,
    })
    try {
      await disconnect(companyConnectorId)
      void onClose()
    } catch {
      toast.error(`Failed to delete connection with ${label}`)
    }
  }

  const handleRemoveMigrationRestrictions = async () => {
    if (!currentConnector) {
      return
    }

    trackRemoveMigrationRestrictions({
      connector_name: label,
      company_connector_id: companyConnectorId,
      service_name: currentConnector.serviceName,
      is_super_admin: isSuperAdmin,
    })

    try {
      // Remove migration flags from metadata while preserving other metadata fields
      const existingMetadata = currentConnector.metadata || {}
      const updatedMetadata = { ...existingMetadata }
      delete updatedMetadata.createdByMigration
      delete updatedMetadata.migrationSource

      await updateCompanyConnector({
        id: companyConnectorId,
        updateCompanyConnectorDto: {
          metadata: updatedMetadata,
        },
      }).unwrap()

      toast.success(
        "Migration restrictions removed. You can now re-authenticate this connector.",
        {
          toastId: "migration-restrictions-removed",
        },
      )
      // updateCompanyConnector already invalidates the CompanyConnectors tag,
      // so subscribers refetch automatically.
    } catch (error) {
      toast.error("Failed to remove migration restrictions", {
        toastId: "migration-restrictions-remove-failed",
      })
    }
  }

  return (
    <ConnectorDialog
      isOpen={isOpen}
      onClose={onClose}
      imgSrc={imgSrc}
      alt={`${label} Logo`}
      title={`Connection to ${label}`}
    >
      <div className="">
        {hasMultipleConnectors || allowMultipleConnections ? (
          <ConnectionList
            companyConnectorId={companyConnectorId}
            isSuperAdmin={isSuperAdmin}
            allowMultipleConnections={allowMultipleConnections}
            onDisconnect={disconnect}
            onReconnect={onReconnect}
            onClose={onClose}
            onAddConnection={onAddConnection}
          />
        ) : isIngestion ? null : (
          <>
            <ConnectorStatus
              companyConnectorId={companyConnectorId}
              connectorStatus={connectorStatus}
              hasError={hasError}
            />

            {grantWarning && (
              <div className="mb-4">
                <CredentialGrantWarningChip warning={grantWarning} wrap />
              </div>
            )}

            {hasError && errorData?.message && (
              <SetupErrorBanner error={errorData} />
            )}

            {isCreatedByMigration && (
              <div className="flex flex-col gap-2 text-left mb-4 p-3 bg-gray-700/50 border border-gray-600 rounded-lg">
                <div className="flex items-center gap-2">
                  {renderStatusIcon("warning")}
                  <p className="text-4 leading-7 font-bold text-platinum">
                    System Managed Connector
                  </p>
                </div>
                <p className="text-4 leading-7 font-medium text-gray-300 ml-8">
                  This connector was created by the system and cannot be
                  re-authenticated through the UI. Please contact your
                  administrator if you need to modify this connection.
                </p>
                {isSuperAdmin && (
                  <div className="mt-3 pt-3 border-t border-gray-600">
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 border border-blue-500 text-white rounded-system-md"
                      onClick={handleRemoveMigrationRestrictions}
                      disabled={isUpdating}
                      isLoading={isUpdating}
                    >
                      {isUpdating
                        ? "Removing restrictions..."
                        : "Remove Migration Restrictions (Admin)"}
                    </Button>
                    <p className="text-3.5 leading-6 font-medium text-gray-400 mt-2">
                      As a super admin, you can remove migration restrictions to
                      allow re-authentication of this connector.
                    </p>
                  </div>
                )}
              </div>
            )}

            <AccountInfo
              confirmedAt={confirmedAt}
              companyConnectorId={companyConnectorId}
            />

            {isSuperAdmin && currentConnector && isMetaSystemUser && (
              <MetaCredentialSection connector={currentConnector} />
            )}

            <CustomConfigSection
              companyConnectorId={companyConnectorId}
              connector={connectorDefinition}
              isSuperAdmin={isSuperAdmin}
            />
          </>
        )}

        {/* Show uploaded files section for ingestion connectors */}
        {isIngestion && (
          <UploadedFilesSection
            companyConnectorId={companyConnectorId}
            metadata={currentConnector?.metadata}
            ingestionConfig={
              connectorDefinition?.metadata?.ingestionConfig as
                | Record<string, unknown>
                | undefined
            }
          />
        )}

        {!isIngestion &&
          !hasMultipleConnectors &&
          !allowMultipleConnections &&
          !isLoadingConnectorStatus &&
          !isHealthy && (
            <div className="flex justify-start w-full gap-4 h-system-2xl">
              {isSuperAdmin ? (
                <Button
                  className="bg-system-blue-700 border border-system-blue-600 text-system-error-400 hover:bg-system-error-400 hover:text-white rounded-system-md"
                  onClick={handleDisconnect}
                >
                  Disconnect
                </Button>
              ) : null}
              {!isCreatedByMigration &&
                RESETTABLE_STATUSES.includes(
                  connectorStatus as ConnectorStatusType,
                ) && (
                  <Button
                    className="bg-system-blue-700 border border-system-blue-600 text-platinum hover:bg-system-blue-600 rounded-system-md"
                    onClick={() => setIsResetDialogOpen(true)}
                    title="Delete your current setup progress and start over"
                  >
                    Start over
                  </Button>
                )}
              <Button
                className="bg-system-brand-pink-600 border border-system-blue-600 text-white rounded-system-md"
                onClick={handleReconnect}
                disabled={isCreatedByMigration}
                title={
                  isCreatedByMigration
                    ? "This connector was created by the system migration and cannot be re-authenticated. Please contact your administrator if you need to modify this connection."
                    : undefined
                }
              >
                Reconnect
              </Button>
            </div>
          )}
        {!isIngestion &&
          !hasMultipleConnectors &&
          !allowMultipleConnections &&
          !isLoadingConnectorStatus &&
          isHealthy &&
          (isSuperAdmin || grantNeedsReconnect) && (
            <div className="flex justify-start w-full gap-4 h-system-2xl">
              {isSuperAdmin ? (
                <Button
                  className="bg-system-blue-700 border border-system-blue-600 text-system-error-400 hover:bg-system-error-400 hover:text-white rounded-system-md"
                  onClick={handleDisconnect}
                >
                  Disconnect
                </Button>
              ) : null}
              {grantNeedsReconnect && (
                <Button
                  className="bg-system-brand-pink-600 border border-system-blue-600 text-white rounded-system-md"
                  onClick={handleReconnect}
                  disabled={isCreatedByMigration}
                  title={
                    isCreatedByMigration
                      ? "This connector was created by the system migration and cannot be re-authenticated. Please contact your administrator if you need to modify this connection."
                      : undefined
                  }
                >
                  Reconnect
                </Button>
              )}
            </div>
          )}
      </div>
      <ResetSetupConfirmDialog
        isOpen={isResetDialogOpen}
        companyConnectorId={companyConnectorId}
        connectorName={label}
        onClose={() => setIsResetDialogOpen(false)}
        onSuccess={onClose}
      />
    </ConnectorDialog>
  )
}

export default ConnectionStatusDialog
